import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  ChartPieSlice, FileText, Buildings, UploadSimple, SignOut, Crown,
  FilePdf, Key, ShieldCheck, Scales, Bell, Sun, Moon, Wallet, MagnifyingGlass, PenNib, PaperPlaneTilt, Calendar,
  ClipboardText, ArrowClockwise, CaretDown, Gear, BookOpen, Target, HardHat, Truck, List, Calculator,
} from "@phosphor-icons/react";
import { API, setActAs } from "@/lib/api";
import { ActAsBanner, ActAsSelector, useActAs } from "@/components/ActAsFornecedor";
import { toast } from "sonner";

/* Grupos do menu lateral — organização por função */
const navGroups = [
  {
    id: "operacoes",
    label: "Sistema",
    sub: "Gestão operacional",
    icon: Gear,
    items: [
      { to: "/dashboard", label: "Painel", icon: ChartPieSlice, testid: "nav-dashboard" },
      { to: "/contratos", label: "Contratos", icon: FileText, testid: "nav-contracts" },
      { to: "/saldos", label: "Saldos", icon: Wallet, testid: "nav-saldos" },
      { to: "/renovacoes-saldo", label: "Renovação de Saldos", icon: ArrowClockwise, testid: "nav-renovacoes" },
      { to: "/aditivos", label: "Aditivos", icon: Scales, testid: "nav-aditivos" },
      { to: "/medicoes", label: "Análise de Medições", icon: HardHat, testid: "nav-medicoes" },
      { to: "/engenharia", label: "Engenharia e Orçamentos", icon: Calculator, testid: "nav-engenharia" },
      { to: "/despachos", label: "Despachos", icon: PaperPlaneTilt, testid: "nav-despachos" },
      { to: "/notificacoes", label: "Notificações", icon: Bell, testid: "nav-notificacoes" },
      { to: "/relatorios", label: "Relatórios", icon: FilePdf, testid: "nav-reports" },
      { to: "/signatarios", label: "Assinantes", icon: PenNib, testid: "nav-signatarios" },
      { to: "/municipios", label: "Municípios", icon: Buildings, testid: "nav-municipalities" },
      { to: "/importar", label: "Importar", icon: UploadSimple, testid: "nav-import" },
      { to: "/admin/usuarios", label: "Gerenciar Acessos", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
      { to: "/admin/fornecedores", label: "Fornecedores", icon: Truck, testid: "nav-admin-fornecedores", adminOnly: true },
      { to: "/fornecedor", label: "Área do Fornecedor", icon: HardHat, testid: "nav-area-fornecedor", adminOnly: true },
    ],
  },
  {
    id: "planejamento",
    label: "Planejamento",
    sub: "Lei 14.133/2021",
    icon: BookOpen,
    items: [
      { to: "/planejamento", label: "Visão Geral", icon: Target, testid: "nav-planejamento" },
      { to: "/demandas", label: "1. Demandas (DFD)", icon: ClipboardText, testid: "nav-demandas" },
      { to: "/etps", label: "2. ETP", icon: BookOpen, testid: "nav-etps" },
      { to: "/pesquisas", label: "3. Pesquisas", icon: MagnifyingGlass, testid: "nav-pesquisas" },
      { to: "/trs", label: "4. TR / Projeto Básico", icon: BookOpen, testid: "nav-trs" },
      { to: "/matrizes-riscos", label: "5. Matriz de Riscos", icon: BookOpen, testid: "nav-matrizes" },
      { to: "/pac", label: "6. Plano Anual (PCA)", icon: Calendar, testid: "nav-pac" },
    ],
  },
];

const fornecedorGroups = [
  {
    id: "fornecedor",
    label: "Área do Fornecedor",
    sub: "Empresa contratada",
    icon: HardHat,
    items: [
      { to: "/fornecedor", label: "Painel", icon: ChartPieSlice, testid: "nav-forn-painel", end: true },
      { to: "/fornecedor/contratos", label: "Meus contratos", icon: FileText, testid: "nav-forn-contratos" },
      { to: "/fornecedor/medicoes", label: "Minhas medições", icon: ClipboardText, testid: "nav-forn-medicoes" },
      { to: "/fornecedor/engenharia", label: "Engenharia e Orçamentos", icon: Calculator, testid: "nav-forn-engenharia" },
    ],
  },
];

function NavSidebarLink({ to, label, icon: Icon, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all ${
          isActive
            ? "bg-[#1E293B] text-[#FFDF00] border-l-2 border-[#FFDF00]"
            : "text-[#94A3B8] hover:text-[#E9EEF5] hover:bg-[#1E293B] border-l-2 border-transparent"
        }`
      }
    >
      <Icon size={16} weight="regular" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

function NavGroup({ group, open, onToggle, visibleItems }) {
  const Icon = group.icon;
  const accent = group.id === "operacoes" ? "text-[#FFDF00]" : "text-[#009C3B]";
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        data-testid={`nav-group-${group.id}`}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-semibold border-l-2 ${
          open ? "border-[#334155] text-[#E9EEF5]" : "border-transparent text-[#94A3B8] hover:text-[#E9EEF5]"
        }`}
      >
        <span className="flex items-center gap-2">
          <Icon size={14} weight="duotone" className={accent} />
          <span>
            <span className="block">{group.label}</span>
            <span className="block text-[9px] text-[#64748B] normal-case tracking-normal font-normal">{group.sub}</span>
          </span>
        </span>
        <CaretDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-1 space-y-0.5 pl-1 border-l border-[#334155]/50">
          {visibleItems.map((it) => <NavSidebarLink key={it.to} {...it} />)}
        </div>
      )}
    </div>
  );
}

async function downloadFiscalCredentials() {
  try {
    const res = await fetch(`${API}/admin/users/credentials`, { credentials: "include" });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const u = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = "fiscais_municipais_credenciais.csv"; a.click();
    window.URL.revokeObjectURL(u);
    toast.success("CSV de credenciais baixado");
  } catch { toast.error("Erro ao baixar credenciais"); }
}

function SidebarFooter({ user, onLogout }) {
  return (
    <div className="px-4 py-4 border-t border-[#334155]">
      {user.role === "admin" && (
        <button
          data-testid="download-credentials-button"
          onClick={downloadFiscalCredentials}
          className="mb-3 w-full flex items-center gap-2 text-xs text-[#F59E0B] hover:text-[#FCD34D] border border-[#F59E0B]/40 hover:border-[#F59E0B] rounded-md px-3 py-2 transition-colors"
        >
          <Key size={14} weight="duotone" /> Baixar credenciais dos fiscais
        </button>
      )}
      <div className="text-xs text-[#94A3B8] mb-1">Sessão ativa</div>
      <div className="text-sm truncate" data-testid="user-email">{user.email}</div>
      <button
        data-testid="logout-button"
        onClick={onLogout}
        className="mt-3 flex items-center gap-2 text-xs text-[#94A3B8] hover:text-[#F59E0B] transition-colors"
      >
        <SignOut size={14} /> Sair
      </button>
    </div>
  );
}

function Sidebar({ user, groups, onLogout, mobileOpen, onNavigate }) {
  const location = window.location.pathname;
  // Auto-abre o grupo que contém a rota ativa
  const initial = {};
  for (const g of groups) {
    initial[g.id] = g.items.some((it) => location.startsWith(it.to));
  }
  // Default: Sistema aberto no primeiro load
  if (!Object.values(initial).some(Boolean)) initial[groups[0]?.id || "operacoes"] = true;
  const [openGroups, setOpenGroups] = useState(initial);
  const toggle = (id) => setOpenGroups((s) => ({ ...s, [id]: !s[id] }));
  return (
    <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-[#0F172A] border-r border-[#334155] flex flex-col z-40 transition-transform md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`} onClick={(e) => { if (e.target.closest("a")) onNavigate?.(); }}>
      <div className="px-6 py-6 border-b border-[#334155]">
        <div className="flex items-center gap-3">
          <Crown weight="duotone" size={30} className="text-[#FFDF00]" />
          <div>
            <div className="font-heading text-lg tracking-tight">Soluções</div>
            <div className="uppercase-label !text-[10px] mt-0.5">Contratuais</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {groups.map((g) => {
          const visible = g.items.filter((it) => !it.adminOnly || user.role === "admin");
          if (!visible.length) return null;
          return (
            <NavGroup key={g.id} group={g} open={!!openGroups[g.id]} onToggle={() => toggle(g.id)} visibleItems={visible} />
          );
        })}
      </nav>
      <SidebarFooter user={user} onLogout={onLogout} />
    </aside>
  );
}

function topBarTexts(user, fornView) {
  if (fornView && user.role === "admin") return ["Área do Fornecedor (modo administrador)", "Soluções Contratuais"];
  if (user.role === "fornecedor") return [user.razao_social || user.name, "Área do Fornecedor"];
  if (user.role === "admin") return ["Sistema de Gestão de Contratos Municipais", "Soluções Contratuais"];
  return [`Painel do Fiscal de ${user.municipio_nome}`, `Prefeitura Municipal de ${user.municipio_nome || ""}`];
}

function TopBar({ user, theme, onToggleTheme, onMenu, fornView }) {
  const [title, subtitle] = topBarTexts(user, fornView);
  return (
    <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="px-4 md:px-10 py-5 flex items-center justify-between gap-3">
        <button className="md:hidden p-2 rounded border border-[#334155]" onClick={onMenu} data-testid="mobile-menu-btn"><List size={18} /></button>
        <div className="min-w-0 flex-1">
          <div className="uppercase-label">{subtitle}</div>
          <div className="font-heading text-xl tracking-tight mt-1">{title}</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={onToggleTheme}
            title={theme === "dark" ? "Tema claro" : "Tema escuro"}
            className="p-2.5 rounded-full border border-[#334155] bg-[#1E293B] text-[#F59E0B] hover:border-[#F59E0B] transition-colors"
          >
            {theme === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
          </button>
          {user.role !== "admin" && user.municipio_nome && (
            <div className="hidden lg:block px-3 py-1.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-xs font-medium">
              Acesso restrito · {user.municipio_nome}
            </div>
          )}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#334155] bg-[#1E293B]">
            <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></span>
            <span className="text-xs text-[#94A3B8]">Sistema online</span>
          </div>
        </div>
      </div>
    </header>
  );
}

export default function Layout() {
  const { user, loading, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const actAs = useActAs();
  const isForn = user?.role === "fornecedor";
  const isAdmin = user?.role === "admin";
  const inForn = location.pathname.startsWith("/fornecedor");
  const fornView = isForn || (isAdmin && inForn);
  const visibleGroups = useMemo(() => (fornView ? fornecedorGroups : navGroups), [fornView]);
  const handleLogout = async () => { setActAs(null); await logout(); navigate(isForn ? "/fornecedor/login" : "/login"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#94A3B8]">Carregando…</div>;
  if (!user) return <Navigate to={inForn ? "/fornecedor/login" : "/login"} replace />;
  if (isForn && !inForn) return <Navigate to="/fornecedor" replace />;
  if (!isForn && !isAdmin && inForn) return <Navigate to="/dashboard" replace />;
  const adminForn = isAdmin && inForn;

  return (
    <div className="min-h-screen bg-[#0F172A] text-[#E9EEF5] grain-overlay">
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}
      <Sidebar key={fornView ? "forn" : "int"} user={user} groups={visibleGroups} onLogout={handleLogout} mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
      <main className="md:ml-64 min-h-screen relative z-10">
        <TopBar user={user} theme={theme} onToggleTheme={toggle} onMenu={() => setMobileOpen(true)} fornView={fornView} />
        {adminForn && actAs && <ActAsBanner actAs={actAs} />}
        <div className="px-4 md:px-10 py-8">
          {adminForn && !actAs ? <ActAsSelector /> : <Outlet key={adminForn ? actAs?.id : "x"} />}
        </div>
      </main>
    </div>
  );
}
